import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import { Formik } from "formik";
import { Modal } from "@material-ui/core";
import { CustomFileUpload } from "../../../common/FormInputs";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { preUploadValidationSchema } from "../../../../helpers/validationSchema";
import { FormLabel } from "../../NoticeGeneration/styles";
import * as XLSX from "xlsx";
import PartyService from "../../../../services/PartyService";
import { getErrorMessage } from "../../../../helpers/functions";
import ContractTable from "./ContractTable";
import FormField from "../../../common/FormField/FormField";
import CustomSelect from "../../NoticeGeneration/component/CustomSelect";

const PreCaseLoad = ({ preModal, setPreModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [modal, setModal] = useState({ state: false });

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        if (accept === ".xlsx") {
          const response = await readExcelFile(
            file,
            fileName,
            setFieldValue,
            values?.contractName,
          );
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file, fileName, setFieldValue, contractName) => {
    const arrayBuffer = await file.arrayBuffer(); // Convert file to ArrayBuffer
    const workbook = XLSX.read(arrayBuffer, { type: "array" }); // Read the workbook
    const sheetName = workbook.SheetNames[0]; // Get the first sheet
    const sheet = workbook.Sheets[sheetName]; // Access the first sheet
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert to 2D array

    const headerRow = data[0]; // First row is the header
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
      contractName,
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    // // Extract values under "CONTRACT NUMBER" column
    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "please wait..." });
      const duplicates = values.contractNo.filter(
        (item, index) => values.contractNo.indexOf(item) !== index,
      );
      if (duplicates.length) {
        setModal({
          state: true,
          metaData: duplicates.map((item) => ({
            contractNo: item,
            caseId: "-",
            createdAt: "-",
          })),
        });
      } else {
        const response = await PartyService.findByContract({
          contractNo: values.contractNo,
          partyId: values?.partyId,
        });
        if (response?.length) {
          setModal({ state: true, metaData: response });
        } else {
          enqueueSnackbar("No duplicate found given the excel", {
            variant: "success",
          });
          setPreModal({ state: false });
        }
      }
    } catch (err) {
      const error = getErrorMessage(err);
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Modal open={preModal?.state} onClose={() => setPreModal({ state: false })}>
      {modal?.state ? (
        <ContractTable metaData={modal?.metaData} {...{ setPreModal }} />
      ) : (
        <DrawerContainer role="presentation">
          <HeadingContainer>
            <Heading>{"Pre Case Load"}</Heading>
            <CloseModal
              onClick={() => setPreModal({ state: false })}
              src={require("../../../../assets/images/closeModal.svg")}
            />
          </HeadingContainer>
          <FormContainer>
            <Formik
              initialValues={{
                contractName: "",
                excel: "",
                contractNo: "",
                partyId: "",
              }}
              validationSchema={preUploadValidationSchema}
              enableReinitialize
              validateOnBlur
              validateOnChange
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldError,
                handleBlur,
                isValid,
              }) => (
                <>
                  <StyledFormElementsWrapper
                    style={{ marginBottom: 8, marginTop: 8 }}
                  >
                    <div style={{ marginBottom: 8}}>
                      <CustomSelect
                        name="partyId"
                        trigger={true}
                        getNoticeTemplates={() => {}}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        menuItems={[]}
                      />
                    </div>
                    {[
                      {
                        name: "contractName",
                        label: "Contract Number",
                        type: "text",
                        required: true,
                      },
                    ].map((input, index) => {
                      return (
                        <FormField
                          key={index}
                          {...{
                            touched,
                            errors,
                            input,
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }}
                        />
                      );
                    })}
                    {[
                      {
                        name: "contractNo",
                        label: "Upload Xlsx",
                        type: "upload",
                        required: true,
                        fileName: "excel",
                        accept: ".xlsx",
                        types: [
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                          "application/vnd.ms-excel",
                          "text/csv",
                        ],
                      },
                    ].map((upload, index) => (
                      <div style={{ marginTop: 10 }}>
                        <FormLabel>
                          {upload.label}{" "}
                          <span className="required-star"> *</span>
                        </FormLabel>
                        <CustomFileUpload
                          error={
                            upload.accept === ".xlsx"
                              ? touched[upload.fileName] &&
                                errors[upload.fileName]
                              : touched[upload.name] && errors[upload.name]
                          }
                          setFile={(file) =>
                            s3Upload(
                              file,
                              upload.name,
                              setFieldValue,
                              upload.types,
                              upload.accept,
                              values,
                              upload.fileName,
                            )
                          }
                          style={{
                            width: "100%",
                          }}
                          accept={upload.accept}
                          file={{
                            name:
                              upload.fileName === "excel"
                                ? values[upload.fileName]
                                : "",
                          }}
                        />
                      </div>
                    ))}
                  </StyledFormElementsWrapper>
                  <Flex>
                    <HalfWidth>
                      <PrimaryOutlinedCloseCTAButton
                        onClick={() => setPreModal({ state: false })}
                      >
                        {"Cancel"}
                      </PrimaryOutlinedCloseCTAButton>
                    </HalfWidth>

                    <HalfWidth>
                      <PrimaryCTAButton onClick={handleSubmit}>
                        {"Submit"}
                      </PrimaryCTAButton>
                    </HalfWidth>
                  </Flex>
                </>
              )}
            </Formik>
          </FormContainer>
        </DrawerContainer>
      )}
    </Modal>
  );
};

export default PreCaseLoad;

const DrawerContainer = styled.div`
  max-width: 95%;
  width: 100%;
  background-color: white;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 516px;
  }
`;

const HeadingContainer = styled.div`
  padding-top: 30px;
  padding-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 30px;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const FormContainer = styled.div`
  padding-top: 14px;
  padding-left: 54px;
  padding-right: 54px;
  padding-bottom: 26px;
  & form {
    padding-top: 10px;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;

const StyledFormElementsWrapper = styled.div`
  margin-bottom: 28px;
  max-width: 436px;
  & .link {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
  }
  & .template {
    margin-top: 0px;
  }
  & .waring-msg {
    color: ${COLORS.WARNING_COLOR};
    font-size: 10px;
    white-space: nowrap;
  }
  & .mediator-type {
    .MuiFormGroup-root {
      display: flex;
      flex-direction: column !important;
      & .MuiFormControlLabel-root {
        padding: 10px 0px;
      }
    }
  }
  @media ${theme?.breakpoints?.sm_down} {
    & .template {
      margin-top: 15px;
    }
  }
`;
